import React, { useEffect, useState, useCallback } from "react";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import "../Users/Users.scss";

// Admin "Payments to Finish". Paid table bookings where a step after the payment failed (server #993):
// the guest was charged but something like the venue's Paid tab row or the promoter earning is missing.
// Finish re-runs only the missing steps; it never takes a second table or posts a second card.

const fmt = (d) => {
  if (!d) return "—";
  try {
    return (
      new Date(d).toLocaleString("en-US", {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }) + " ET"
    );
  } catch {
    return "—";
  }
};

const money = (cents) =>
  typeof cents === "number" ? `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";

const PaymentsToFinish = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [working, setWorking] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(false);
    try {
      const { data } = await api.get("/bottle-service-admin/payment-followups");
      setRows(data?.data || []);
    } catch (e) {
      setErr(true);
      setRows([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const finish = async (row) => {
    if (working) return;
    setWorking(row.id);
    setNotice(null);
    try {
      const { data } = await api.post(`/bottle-service-admin/payment-followups/${row.id}/finish`);
      setNotice(data?.ok
        ? { ok: true, text: `Finished ${row.guest || "the booking"} at ${row.venue || "the venue"}.` }
        : { ok: false, text: `Still missing: ${(data?.labels || []).join(", ")}. Try again in a minute.` });
    } catch (e) {
      setNotice({ ok: false, text: e?.response?.data?.message || "Couldn't finish this booking. Try again." });
    }
    setWorking(null);
    load();
  };

  const cell = { padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,.08)", fontSize: 13, verticalAlign: "top" };
  const head = { ...cell, color: "rgba(255,255,255,.6)", fontWeight: 600, textAlign: "left", whiteSpace: "nowrap" };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          <h1 style={{ color: "#fff", marginBottom: 6 }}>Payments to Finish</h1>
          <p style={{ color: "rgba(255,255,255,.6)", marginBottom: 18, fontSize: 14 }}>
            Guests who paid for a table where a step after the payment didn't finish. Finish redoes only
            the missing steps.
          </p>

          {notice && (
            <p style={{ color: notice.ok ? "#63D67A" : "#ff8a8a", marginBottom: 14, fontSize: 14 }}>{notice.text}</p>
          )}
          {loading && <p style={{ color: "rgba(255,255,255,.6)" }}>Loading…</p>}
          {err && <p style={{ color: "#ff8a8a" }}>Could not load payments to finish.</p>}
          {!loading && !err && rows.length === 0 && (
            <p style={{ color: "rgba(255,255,255,.6)" }}>Nothing to finish. Every paid booking went through.</p>
          )}

          {!loading && !err && rows.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff", minWidth: 900 }}>
                <thead>
                  <tr>
                    <th style={head}>Failed</th>
                    <th style={head}>Guest</th>
                    <th style={head}>Venue</th>
                    <th style={head}>Minimum</th>
                    <th style={head}>Missing</th>
                    <th style={head}>Payment</th>
                    <th style={head} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td style={cell}>{fmt(r.failed_at)}</td>
                      <td style={cell}>{r.guest || "—"}</td>
                      <td style={cell}>{r.venue || "—"}</td>
                      <td style={cell}>{money(r.minimum_cents)}</td>
                      <td style={cell}>
                        {(r.missing || []).join(", ")}
                        {r.last_error ? <div style={{ color: "rgba(255,255,255,.45)", fontSize: 12, marginTop: 4 }}>{r.last_error}</div> : null}
                      </td>
                      <td style={{ ...cell, fontFamily: "monospace", fontSize: 12 }}>{r.payment_intent_id}</td>
                      <td style={cell}>
                        <button
                          type="button"
                          onClick={() => finish(r)}
                          disabled={!!working}
                          style={{
                            padding: "8px 16px",
                            borderRadius: 10,
                            border: "none",
                            background: "#4F30EA",
                            color: "#fff",
                            cursor: working ? "default" : "pointer",
                            opacity: working && working !== r.id ? 0.5 : 1,
                            fontSize: 13,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {working === r.id ? "Finishing…" : "Finish"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>
      </div>
    </main>
  );
};

export default PaymentsToFinish;
